import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Avatar,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  LockOutlined as LockIcon,
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
} from '@mui/icons-material';
import AzureAuthService from 'services/AzureAuthService';
import GoogleAuthService from 'services/GoogleAuthService';
import { useAuth } from 'contexts/AuthContext';
import { useAzureAuth } from 'contexts/AzureAuthContext';
import { getRuntimeConfig } from 'runtime/runtimeConfig';
import { useTranslation } from 'react-i18next';

const LoginForm: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { login, isLoading } = useAuth();
  const { t } = useTranslation('auth');
  const errorRef = useRef<string>('');
  const { isEnabled } = useAzureAuth();

  const [isGoogleEnabled, setIsGoogleEnabled] = useState(false);

  // Detect if Google EasyAuth provider is enabled by probing `/google-auth`.
  useEffect(() => {
    let isMounted = true;

    const checkGoogle = async () => {
      // `false` from /runtime-config means this origin has no Google binding.
      if (getRuntimeConfig().auth.google_enabled === false) {
        if (isMounted) setIsGoogleEnabled(false);
        return;
      }
      try {
        const googleAuthData = await GoogleAuthService.checkGoogleAuth();
        if (isMounted) {
          setIsGoogleEnabled(googleAuthData?.is_enabled ?? false);
        }
      } catch {
        if (isMounted) {
          setIsGoogleEnabled(false);
        }
      }
    };

    checkGoogle();

    return () => {
      isMounted = false;
    };
  }, []);
  
  // Debug: log isEnabled value
  useEffect(() => {
    console.log('🔍 [LOGIN FORM] Azure auth isEnabled:', isEnabled);
  }, [isEnabled]);
  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    errorRef.current = '';
    // Clear global error fallback
    (window as any).loginError = '';

    if (!username.trim() || !password.trim()) {
      const validationError = t('errors.requiredFields');
      setError(validationError);
      errorRef.current = validationError;
      (window as any).loginError = validationError;
      return;
    }

    try {
      await login(username.trim(), password);
    } catch (error: any) {
      const errorMessage = error?.message || error?.toString() || t('errors.loginFailed');
      setError(errorMessage);
      errorRef.current = errorMessage;
      // Global fallback for browser extension interference
      (window as any).loginError = errorMessage;
    }
  }, [username, password, login, t]);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleMicrosoftLogin = useCallback(() => {
    AzureAuthService.initiateMicrosoftLogin();
  }, []);

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
                <LockIcon />
              </Avatar>
              <Typography component="h1" variant="h5" textAlign="center">
                {t('title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
                {t('subtitle')}
              </Typography>
              {(isEnabled || isGoogleEnabled) && (
                <>
                  {isEnabled && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
                      {t('azureAuthEnabled')}
                    </Typography>
                  )}
                  {isGoogleEnabled && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
                      {t('googleAuthEnabled')}
                    </Typography>
                  )}
                </>
              )}
            </Box>

            {(() => {
              const currentError = error || errorRef.current || (window as any).loginError;
              return currentError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {currentError}
                </Alert>
              );
            })()}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label={t('username')}
                name="username"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label={t('password')}
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t('togglePasswordVisibility')}
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        disabled={isLoading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  t('signIn')
                )}
              </Button>
              {(isEnabled || isGoogleEnabled) && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                    <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                    <Typography variant="body2" sx={{ px: 2, color: 'text.secondary' }}>
                      {t('or')}
                    </Typography>
                    <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                  </Box>

                  {isEnabled && (
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleMicrosoftLogin}
                      disabled={isLoading}
                      sx={{
                        mb: isGoogleEnabled ? 2 : 0,
                        py: 1.5,
                        borderColor: '#0078d4',
                        color: '#0078d4',
                        '&:hover': {
                          borderColor: '#106ebe',
                          backgroundColor: 'rgba(0, 120, 212, 0.04)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <svg
                          width="21"
                          height="21"
                          viewBox="0 0 21 21"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect x="0" y="0" width="10" height="10" fill="#F25022" />
                          <rect x="11" y="0" width="10" height="10" fill="#7FBA00" />
                          <rect x="0" y="11" width="10" height="10" fill="#00A4EF" />
                          <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
                        </svg>
                        {t('loginWithMicrosoft')}
                      </Box>
                    </Button>
                  )}

                  {isGoogleEnabled && (
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => GoogleAuthService.initiateGoogleLogin()}
                      disabled={isLoading}
                      sx={{
                        py: 1.5,
                        borderColor: '#4285F4',
                        color: '#4285F4',
                        '&:hover': {
                          borderColor: '#3b73e6',
                          backgroundColor: 'rgba(66, 133, 244, 0.04)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21.805 10.023h-9.812v3.643h5.613c-.24 1.46-1.57 4.286-5.613 4.286-3.38 0-6.13-2.787-6.13-6.215 0-3.429 2.75-6.216 6.13-6.216 1.92 0 3.206.82 3.944 1.53l2.687-2.587C15.698 2.931 13.393 1.8 11.993 1.8 6.904 1.8 2.8 5.943 2.8 11.037c0 5.094 4.104 9.237 9.193 9.237 5.323 0 8.842-3.737 8.842-9.001 0-.602-.064-1.065-.13-1.25Z"
                            fill="#4285F4"
                          />
                          <path
                            d="M9.812 14.45 8.98 19.32c.947.35 1.97.543 3.012.543 2.82 0 5.19-1.05 6.91-2.79l-3.08-2.39c-.83.6-1.86.97-3.83.97-1.3 0-2.4-.42-3.15-1.2Z"
                            fill="#34A853"
                            opacity="0.95"
                          />
                        </svg>
                        {t('loginWithGoogle')}
                      </Box>
                    </Button>
                  )}
                </>
              )}
            </Box>

          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default LoginForm;
